import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './Icon';
import { io } from 'socket.io-client';
import API from '../services/api';
import './ChatWidget.css';

const EMOJIS = [
  '😀','😂','😍','😎','😢','😡','👍','👎','❤️','🔥','🎉','💯',
  '😊','🙏','👋','💪','🤔','😴','🥳','😱','🤣','😏','🥰','😇',
  '👀','✅','❌','⭐','🚀','💡','📎','📷','🎵','🎮','🏆','💰'
];

let _socket = null;
function getSocket(token) {
  if (!_socket || !_socket.connected) {
    _socket = io(window.location.origin, {
      auth: { token },
      transports: ['websocket', 'polling']
    });
  }
  return _socket;
}

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1100].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.18);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + i * 0.12 + 0.18);
    });
  } catch (e) {}
}

export default function ChatWidget() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');


  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // list | chat
  const [socket, setSocket] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingMap, setTypingMap] = useState({});
  const [showEmoji, setShowEmoji] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  // Call state
  const [callState, setCallState] = useState(null);
  const [callData, setCallData] = useState(null);
  const peersRef = useRef({});
  const localStreamRef = useRef(null);
  const callIdRef = useRef(null);
  const participantsRef = useRef([]);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimer = useRef(null);
  const activeConvRef = useRef(null);
  const openRef = useRef(false);

  useEffect(() => { activeConvRef.current = activeConv; }, [activeConv]);
  useEffect(() => { openRef.current = open; }, [open]);

  // Register service worker + request notification permission
  const swRef = useRef(null);
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(reg => {
        swRef.current = reg;
      }).catch(() => {});
    }
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const s = getSocket(token);
    setSocket(s);

    s.on('online:list', ids => setOnlineUsers(new Set(ids)));
    s.on('user:online', ({ userId, online }) => {
      setOnlineUsers(prev => { const n = new Set(prev); online ? n.add(userId) : n.delete(userId); return n; });
    });

    s.on('message:new', (msg) => {
      const conv = activeConvRef.current;
      if (conv && msg.conversation_id === conv.id) {
        setMessages(prev => [...prev, msg]);
        if (msg.sender_id !== user.id) {
          API.post('/conversations/' + conv.id + '/read').catch(() => {});
        }
      }

      setConversations(prev => {
        const updated = prev.map(c =>
          c.id === msg.conversation_id
            ? {
                ...c,
                last_message: msg.content || (msg.type === 'image' ? '[Şəkil]' : '[Fayl]'),
                last_message_type: msg.type,
                last_message_at: msg.created_at,
                unread_count: (conv?.id === c.id && openRef.current)
                  ? 0
                  : (c.unread_count || 0) + (msg.sender_id !== user.id ? 1 : 0)
              }
            : c
        );
        return updated.sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0));
      });

      if (msg.sender_id !== user.id) {
        playBeep();

        // In-app toast (always works)
        const toastId = Date.now();
        setToasts(prev => [...prev, { id: toastId, msg }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toastId)), 5000);

        // Update page title with unread count
        const unread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0) + 1;
        document.title = '(' + unread + ') Yeni mesaj — EMS';

        // Desktop notification
        if ('Notification' in window && Notification.permission === 'granted') {
          const title = msg.sender_name || 'Yeni mesaj';
          const body = msg.content || (msg.type === 'image' ? 'Şəkil' : 'Fayl');
          try {
            if (swRef.current) {
              swRef.current.showNotification(title, {
                body, icon: '/mtk-ikon-192.png', badge: '/mtk-ikon-192.png',
                tag: 'ems-chat-' + msg.conversation_id, renotify: true,
              }).catch(() => {});
            }
            // Also try direct Notification as fallback
            const n = new Notification(title, { body, icon: '/mtk-ikon-192.png' });
            n.onclick = () => { window.focus(); n.close(); };
          } catch(e) {}
        }
      }
    });

    s.on('message:read', ({ conversation_id, user_id: readBy }) => {
      if (readBy === user.id) return;
      // Update read_count on all messages in this conversation
      setMessages(prev => prev.map(m =>
        m.conversation_id === conversation_id && m.sender_id === user.id
          ? { ...m, read_count: (m.read_count || 0) + 1 }
          : m
      ));
    });

    s.on('message:deleted', ({ message_id }) => {
      setMessages(prev => prev.map(m => m.id === message_id ? { ...m, is_deleted: 1 } : m));
    });

    s.on('typing:start', ({ userId: uid, conversation_id, name }) => {
      if (activeConvRef.current?.id === conversation_id && uid !== user.id) {
        setTypingMap(p => ({ ...p, [uid]: name }));
      }
    });
    s.on('typing:stop', ({ userId: uid }) => {
      setTypingMap(p => { const n = {...p}; delete n[uid]; return n; });
    });

    // Call events
    s.on('call:incoming', (data) => {
      setCallData(data);
      setCallState('incoming');
      playRingtone();
    });
    s.on('call:started', (data) => { callIdRef.current = data.call_id; });
    s.on('call:accepted', (data) => {
      setCallState('active');
      stopRingtone();
      startPeerConnection(data.user_id, true, s);
    });
    s.on('call:rejected', () => {
      stopRingtone();
      setCallState(null); setCallData(null);
      endLocalStream();
    });
    s.on('call:ended', () => endCallCleanup());
    s.on('call:signal', ({ from_user_id, signal, call_id }) => {
      if (call_id !== callIdRef.current) return;
      if (peersRef.current[from_user_id]) {
        peersRef.current[from_user_id].signal(signal);
      } else {
        startPeerConnection(from_user_id, false, s, signal);
      }
    });

    // Real-time ticket list events - broadcast to other components via window events
    s.on('ticket:new', (ticket) => {
      window.dispatchEvent(new CustomEvent('ems:ticket:new', { detail: ticket }));
    });
    s.on('ticket:updated', (ticket) => {
      window.dispatchEvent(new CustomEvent('ems:ticket:updated', { detail: ticket }));
    });
    s.on('leave:new', (leave) => {
      window.dispatchEvent(new CustomEvent('ems:leave:new', { detail: leave }));
    });
    s.on('leave:updated', (leave) => {
      window.dispatchEvent(new CustomEvent('ems:leave:updated', { detail: leave }));
    });
    s.on('leave:notify', (data) => {
      playBeep();
      const toastId = Date.now();
      const msgs = {
        new_request: 'Yeni işburaxma ərizəsi',
        awaiting_hr: 'HR təsdiqini gözləyir',
        awaiting_director: 'Direktor imzasını gözləyir',
        approved: 'İşburaxma ərizəniz təsdiqləndi',
        rejected: 'İşburaxma ərizəsi rədd edildi',
      };
      setToasts(prev => [...prev, {
        id: toastId, ticketNotif: true, type: 'leave',
        subType: data.type, leaveId: data.leaveId,
        title: msgs[data.type] || 'İşburaxma bildirişi',
        employee: data.employeeName || ''
      }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toastId)), 6000);
    });

    // Ticket notifications
    s.on('ticket:notify', (data) => {
      const isAssigned = data.type === 'assigned';
      playBeep();
      const toastId = Date.now();
      setToasts(prev => [...prev, {
        id: toastId,
        ticketNotif: true,
        type: data.type,
        ticketId: data.ticketId,
        title: data.title,
        timeSpent: data.timeSpent
      }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toastId)), 6000);
    });

    loadConversations(s);
    API.get('/users').then(r => setAllUsers(r.data)).catch(() => {});

    return () => {
      ['online:list','user:online','message:new','message:deleted','message:read','typing:start','typing:stop',
       'call:incoming','call:started','call:accepted','call:rejected','call:ended','call:signal',
       'ticket:notify', 'ticket:new', 'ticket:updated',
       'leave:new', 'leave:updated', 'leave:notify'
      ].forEach(e => s.off(e));
    };
  }, []);

  const loadConversations = async (s) => {
    try {
      const res = await API.get('/conversations');
      setConversations(res.data);
      const unread = res.data.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setTotalUnread(unread);
      if (s) res.data.forEach(c => s.emit('conversation:join', c.id));
    } catch (e) {}
  };

  useEffect(() => {
    const total = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);
    setTotalUnread(total);
  }, [conversations]);

  const openConv = async (conv) => {
    setActiveConv(conv);
    setView('chat');
    setMessages([]);
    try {
      const res = await API.get('/conversations/' + conv.id + '/messages', { params: { limit: 50 } });
      setMessages(res.data);
      await API.post('/conversations/' + conv.id + '/read');
      setConversations(prev => {
        const updated = prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c);
        const totalUnread = updated.reduce((s, c) => s + (c.unread_count || 0), 0);
        document.title = totalUnread > 0 ? '(' + totalUnread + ') EMS' : 'Elektron Məktəb Sistemi';
        return updated;
      });
      socket && socket.emit('conversation:join', conv.id);
    } catch (e) {}
    setTimeout(() => messagesEndRef.current?.scrollIntoView(), 80);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (!text.trim() || !activeConv || !socket) return;
    socket.emit('message:send', { conversation_id: activeConv.id, content: text.trim(), type: 'text' });
    setText('');
    setShowEmoji(false);
    clearTimeout(typingTimer.current);
    socket.emit('typing:stop', { conversation_id: activeConv.id });
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
    if (!socket || !activeConv) return;
    socket.emit('typing:start', { conversation_id: activeConv.id });
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => socket.emit('typing:stop', { conversation_id: activeConv.id }), 2000);
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeConv) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await API.post('/chat/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      socket.emit('message:send', {
        conversation_id: activeConv.id,
        content: res.data.name,
        type: res.data.type,
        file_url: res.data.url,
        file_name: res.data.name,
        file_size: res.data.size
      });
    } catch (e) { alert('Fayl yüklənmədi'); }
    e.target.value = '';
  };

  const deleteMsg = (m) => {
    if (m.sender_id !== user.id) return;
    socket.emit('message:delete', { message_id: m.id, conversation_id: activeConv.id });
  };

  const startDirect = async (uid) => {
    try {
      const res = await API.post('/conversations/direct', { user_id: uid });
      await loadConversations(socket);
      const all = await API.get('/conversations');
      setConversations(all.data);
      const found = all.data.find(c => c.id === res.data.id);
      if (found) { openConv(found); }
      setShowNewChat(false);
    } catch (e) { alert('Xəta'); }
  };

  const createGroup = async () => {
    if (!groupName.trim() || selectedUsers.length < 1) return;
    try {
      await API.post('/conversations/group', { name: groupName, member_ids: selectedUsers });
      await loadConversations(socket);
      setShowNewGroup(false); setGroupName(''); setSelectedUsers([]);
    } catch (e) { alert('Xəta'); }
  };

  // --- CALLS ---
  const ringtoneRef = useRef(null);
  const playRingtone = () => {
    let i = 0;
    ringtoneRef.current = setInterval(() => {
      playBeep(); i++;
      if (i > 15) stopRingtone();
    }, 1500);
  };
  const stopRingtone = () => { clearInterval(ringtoneRef.current); ringtoneRef.current = null; };

  const startCall = async () => {
    if (!activeConv) return;
    const members = activeConv.members?.filter(m => m.id !== user.id).map(m => m.id) || [];
    if (members.length === 0) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      participantsRef.current = members;
      socket.emit('call:invite', { conversation_id: activeConv.id, call_type: 'audio', to_user_ids: members });
      setCallState('calling');
    } catch (e) { alert('Mikrofona icazə lazımdır'); }
  };

  const acceptCall = async () => {
    stopRingtone();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      callIdRef.current = callData.call_id;
      participantsRef.current = [callData.from_user_id];
      socket.emit('call:accept', { call_id: callData.call_id, from_user_id: callData.from_user_id });
      setCallState('active');
      // Acceptor must create peer connection (non-initiator)
      await startPeerConnection(callData.from_user_id, false, socket);
    } catch (e) { rejectCall(); }
  };

  const rejectCall = () => {
    stopRingtone();
    socket.emit('call:reject', { call_id: callData.call_id, from_user_id: callData.from_user_id });
    setCallState(null); setCallData(null);
  };

  const remoteAudiosRef = useRef({});

  const endCallCleanup = () => {
    stopRingtone();
    endLocalStream();
    Object.values(peersRef.current).forEach(p => { try { p.destroy(); } catch (e) {} });
    peersRef.current = {};
    // Clean up remote audio DOM elements
    Object.values(remoteAudiosRef.current).forEach(a => {
      if (a) { a.srcObject = null; if (a.parentNode) a.parentNode.removeChild(a); }
    });
    remoteAudiosRef.current = {};
    setCallState(null); setCallData(null);
  };

  const endCall = () => {
    socket.emit('call:end', { call_id: callIdRef.current, participant_ids: participantsRef.current });
    endCallCleanup();
  };

  const endLocalStream = () => {
    if (localStreamRef.current) { localStreamRef.current.getTracks().forEach(t => t.stop()); localStreamRef.current = null; }
  };

  const startPeerConnection = async (targetId, initiator, s, signal) => {
    const SimplePeer = (await import('simple-peer')).default;
    const stream = localStreamRef.current;
    if (!stream) return;
    const peer = new SimplePeer({ initiator, stream, trickle: true });
    peer.on('signal', sig => s.emit('call:signal', { to_user_id: targetId, signal: sig, call_id: callIdRef.current }));
    peer.on('stream', remoteStream => {
      let audio = remoteAudiosRef.current[targetId];
      if (!audio) {
        audio = document.createElement('audio');
        audio.autoplay = true;
        audio.playsInline = true;
        audio.setAttribute('autoplay', '');
        document.body.appendChild(audio);
        remoteAudiosRef.current[targetId] = audio;
      }
      audio.srcObject = remoteStream;
      audio.play().catch((err) => {
        console.warn('Audio play failed, retrying...', err);
        const retry = () => { audio.play().catch(() => {}); document.removeEventListener('click', retry); };
        document.addEventListener('click', retry);
      });
    });
    peer.on('error', (err) => { console.warn('Peer error:', err); });
    peer.on('close', () => {
      const audio = remoteAudiosRef.current[targetId];
      if (audio) { audio.srcObject = null; if (audio.parentNode) audio.parentNode.removeChild(audio); }
      delete remoteAudiosRef.current[targetId];
      delete peersRef.current[targetId];
    });
    if (signal) peer.signal(signal);
    peersRef.current[targetId] = peer;
  };

  const fmt = (d) => new Date(d).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' });
  const fmtDate = (d) => {
    const dt = new Date(d), today = new Date();
    if (dt.toDateString() === today.toDateString()) return 'Bu gün';
    const yest = new Date(today); yest.setDate(today.getDate() - 1);
    if (dt.toDateString() === yest.toDateString()) return 'Dünən';
    return dt.toLocaleDateString('az-AZ');
  };

  const convDisplayName = (c) => c.type === 'group' ? c.name : (c.name || '?');
  const isOnline = (c) => c.type === 'direct' && c.other_user_id && onlineUsers.has(c.other_user_id);

  const filteredUsers = allUsers.filter(u => u.id !== user.id && u.name.toLowerCase().includes(userSearch.toLowerCase()));

  // Group messages by date
  const grouped = [];
  let lastDate = null;
  messages.forEach(m => {
    const d = fmtDate(m.created_at);
    if (d !== lastDate) { grouped.push({ type: 'date', label: d }); lastDate = d; }
    grouped.push({ type: 'msg', data: m });
  });

  const typingNames = Object.values(typingMap);

  if (!token) return null;
  return (
    <>
      {/* Toast notifications */}
      <div className="cw-toasts">
        {toasts.map(t => t.ticketNotif ? (
          <div key={t.id} className="cw-toast cw-toast-ticket" onClick={() => setToasts(p => p.filter(x => x.id !== t.id))}>
            <div className="cw-toast-avatar" style={{ background: t.type === 'leave' ? '#7c3aed' : t.type === 'assigned' ? '#007c82' : '#059669' }}>
              {t.type === 'leave' ? <Icon name="clipboard" size={15} /> : t.type === 'assigned' ? <Icon name="ticket" size={15} /> : <Icon name="check" size={15} />}
            </div>
            <div className="cw-toast-body">
              <div className="cw-toast-name">{t.title}</div>
              <div className="cw-toast-msg">
                {t.type === 'leave' ? (t.employee || '') : ('#' + t.ticketId + ' — ' + t.title)}
              </div>
            </div>
            <button className="cw-toast-close" onClick={e => { e.stopPropagation(); setToasts(p => p.filter(x => x.id !== t.id)); }}><Icon name="close" size={16} /></button>
          </div>
        ) : (
          <div key={t.id} className="cw-toast" onClick={() => {
            setOpen(true);
            const c = conversations.find(c => c.id === t.msg.conversation_id);
            if (c) openConv(c);
            setToasts(p => p.filter(x => x.id !== t.id));
          }}>
            <div className="cw-toast-avatar">{(t.msg.sender_name || '?').charAt(0)}</div>
            <div className="cw-toast-body">
              <div className="cw-toast-name">{t.msg.sender_name}</div>
              <div className="cw-toast-msg">
                {t.msg.type === 'image' ? <><Icon name="camera" size={12} /> Şəkil</> : t.msg.type === 'file' ? <><Icon name="paperclip" size={12} /> Fayl</> : t.msg.content}
              </div>
            </div>
            <button className="cw-toast-close" onClick={e => { e.stopPropagation(); setToasts(p => p.filter(x => x.id !== t.id)); }}><Icon name="close" size={16} /></button>
          </div>
        ))}
      </div>

      {/* Call overlay */}
      {callState && (
        <div className="cw-call-overlay">
          <div className="cw-call-box">
            <div className="cw-call-avatar">{(callState === 'incoming' ? callData?.from_name : convDisplayName(activeConv || {})).charAt(0)}</div>
            <div className="cw-call-name">{callState === 'incoming' ? callData?.from_name : convDisplayName(activeConv || {})}</div>
            <div className="cw-call-status">
              {callState === 'incoming' && 'Gələn zəng...'}
              {callState === 'calling' && 'Zəng edilir...'}
              {callState === 'active' && 'Zəng davam edir'}
            </div>
            <div className="cw-call-btns">
              {callState === 'incoming' && (
                <button className="cw-call-btn accept" onClick={acceptCall}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1.14h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 6.85a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                </button>
              )}
              <button className="cw-call-btn reject" onClick={callState === 'incoming' ? rejectCall : endCall}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="22" height="22"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div className="cw-panel">
          {/* Header */}
          <div className="cw-panel-header">
            {view === 'chat' ? (
              <>
                <button className="cw-back" onClick={() => setView('list')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
                </button>
                <div className="cw-header-avatar">{convDisplayName(activeConv).charAt(0)}</div>
                <div className="cw-header-info">
                  <span className="cw-header-name">{convDisplayName(activeConv)}</span>
                  <span className="cw-header-sub">{isOnline(activeConv) ? 'Onlayn' : activeConv?.type === 'group' ? (activeConv.members?.length || 0) + ' üzv' : 'Oflayn'}</span>
                </div>
                <button className="cw-call-trigger" onClick={startCall} title="Səsli zəng">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1.14h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 6.85a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                </button>
              </>
            ) : (
              <>
                <span className="cw-header-title">Mesajlar</span>
                <div className="cw-header-actions">
                  <button onClick={() => setShowNewChat(true)} title="Yeni söhbət">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                  </button>
                  <button onClick={() => setShowNewGroup(true)} title="Qrup">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                  </button>
                </div>
              </>
            )}
            <button className="cw-close" onClick={() => setOpen(false)}><Icon name="close" size={16} /></button>
          </div>

          {/* Body */}
          {view === 'list' ? (
            <div className="cw-conv-list">
              {conversations.length === 0 && <p className="cw-empty">Söhbət yoxdur. Yeni söhbət başladın.</p>}
              {conversations.map(c => (
                <div key={c.id} className="cw-conv-item" onClick={() => openConv(c)}>
                  <div className="cw-conv-av-wrap">
                    <div className="cw-conv-av">{convDisplayName(c).charAt(0).toUpperCase()}</div>
                    {isOnline(c) && <span className="cw-dot"></span>}
                  </div>
                  <div className="cw-conv-info">
                    <div className="cw-conv-row">
                      <span className={'cw-conv-name' + (c.unread_count > 0 ? ' unread' : '')}>{convDisplayName(c)}</span>
                      <span className="cw-conv-time">{c.last_message_at ? fmt(c.last_message_at) : ''}</span>
                    </div>
                    <div className="cw-conv-row">
                      <span className={'cw-conv-last' + (c.unread_count > 0 ? ' unread' : '')}>
                        {c.last_message_type === 'image' ? <><Icon name="camera" size={12} /> Şəkil</> : c.last_message_type === 'file' ? <><Icon name="paperclip" size={12} /> Fayl</> : c.last_message || ''}
                      </span>
                      {c.unread_count > 0 && <span className="cw-unread">{c.unread_count}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="cw-messages">
                {grouped.map((item, i) => {
                  if (item.type === 'date') return (
                    <div key={'d' + i} className="cw-date-sep"><span>{item.label}</span></div>
                  );
                  const m = item.data;
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={'cw-msg' + (mine ? ' mine' : '')}>
                      {!mine && activeConv?.type === 'group' && (
                        <div className="cw-msg-sender">{m.sender_name}</div>
                      )}
                      <div className={'cw-bubble' + (m.is_deleted ? ' deleted' : '') + (mine ? ' mine' : '')}>
                        {m.is_deleted ? (
                          <span className="cw-deleted">Mesaj silindi</span>
                        ) : m.type === 'image' ? (
                          <a href={m.file_url} target="_blank" rel="noreferrer">
                            <img src={m.file_url} alt="" className="cw-img" />
                          </a>
                        ) : m.type === 'file' ? (
                          <a href={m.file_url} target="_blank" rel="noreferrer" className="cw-file-link">
                            <Icon name="paperclip" size={13} /> {m.file_name}
                          </a>
                        ) : (
                          <span>{m.content}</span>
                        )}
                        <div className="cw-msg-meta">
                          <span className="cw-msg-time">{fmt(m.created_at)}</span>
                          {mine && !m.is_deleted && (
                            <span className={'cw-read-tick' + (m.read_count > 0 ? ' read' : '')}
                              title={m.read_count > 0 ? 'Oxundu' : 'Göndərildi'}>
                              {m.read_count > 0 ? <Icon name="checkDouble" size={13} /> : <Icon name="check" size={11} />}
                            </span>
                          )}
                          {mine && !m.is_deleted && (
                            <button className="cw-del-btn" onClick={() => deleteMsg(m)}><Icon name="close" size={16} /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {typingNames.length > 0 && (
                  <div className="cw-typing">{typingNames.join(', ')} yazır<span className="cw-dots"><span/><span/><span/></span></div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {showEmoji && (
                <div className="cw-emoji-grid">
                  {EMOJIS.map(e => (
                    <button key={e} className="cw-emoji-btn" onClick={() => { setText(p => p + e); setShowEmoji(false); }}>{e}</button>
                  ))}
                </div>
              )}

              <form className="cw-input" onSubmit={sendMessage}>
                <button type="button" className="cw-icon-btn" onClick={() => setShowEmoji(p => !p)}><Icon name="smile" size={18} /></button>
                <button type="button" className="cw-icon-btn" onClick={() => fileInputRef.current?.click()}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                </button>
                <input ref={fileInputRef} type="file" style={{ display:'none' }} onChange={handleFile} accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt" />
                <input
                  className="cw-text-input"
                  value={text}
                  onChange={handleTextChange}
                  placeholder="Mesaj..."
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) sendMessage(e); }}
                />
                <button type="submit" className="cw-send" disabled={!text.trim()}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22,2 15,22 11,13 2,9"/></svg>
                </button>
              </form>
            </>
          )}

          {/* New direct chat */}
          {showNewChat && (
            <div className="cw-subpanel">
              <div className="cw-subpanel-header">
                <span>Yeni söhbət</span>
                <button onClick={() => { setShowNewChat(false); setUserSearch(''); }}><Icon name="close" size={16} /></button>
              </div>
              <input className="cw-search" placeholder="Ad axtar..." value={userSearch} onChange={e => setUserSearch(e.target.value)} />
              <div className="cw-user-list">
                {filteredUsers.map(u => (
                  <div key={u.id} className="cw-user-item" onClick={() => startDirect(u.id)}>
                    <div className="cw-conv-av sm">{u.name.charAt(0)}</div>
                    <div>
                      <div className="cw-ui-name">{u.name}</div>
                      <div className="cw-ui-role">{u.role}</div>
                    </div>
                    {onlineUsers.has(u.id) && <span className="cw-dot static"></span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New group */}
          {showNewGroup && (
            <div className="cw-subpanel">
              <div className="cw-subpanel-header">
                <span>Qrup yarat</span>
                <button onClick={() => { setShowNewGroup(false); setUserSearch(''); setSelectedUsers([]); setGroupName(''); }}><Icon name="close" size={16} /></button>
              </div>
              <input className="cw-search" placeholder="Qrup adı..." value={groupName} onChange={e => setGroupName(e.target.value)} />
              <input className="cw-search" style={{ marginTop: 6 }} placeholder="Üzv axtar..." value={userSearch} onChange={e => setUserSearch(e.target.value)} />
              <div className="cw-user-list">
                {filteredUsers.map(u => (
                  <div key={u.id} className={'cw-user-item' + (selectedUsers.includes(u.id) ? ' sel' : '')}
                    onClick={() => setSelectedUsers(p => p.includes(u.id) ? p.filter(x => x !== u.id) : [...p, u.id])}>
                    <div className="cw-conv-av sm">{u.name.charAt(0)}</div>
                    <div className="cw-ui-name">{u.name}</div>
                    {selectedUsers.includes(u.id) && <span style={{ marginLeft:'auto', color:'#007c82', fontWeight:700 }}><Icon name="check" size={15} /></span>}
                  </div>
                ))}
              </div>
              <div className="cw-group-footer">
                <span>{selectedUsers.length} seçildi</span>
                <button className="cw-create-btn" onClick={createGroup} disabled={!groupName.trim() || selectedUsers.length === 0}>Yarat</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating button */}
      <button className="cw-fab" onClick={() => { setOpen(p => !p); if (!open) setView('list'); }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
        {totalUnread > 0 && <span className="cw-fab-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>}
      </button>
    </>
  );
}
